import { inject, Injectable } from '@angular/core';
import { catchError, EMPTY, firstValueFrom, map, Observable, of, shareReplay, throwError } from 'rxjs';
import { Page } from '../../../@common/entities/page.interface';
import { Logger } from '../../../@common/log/logger';
import { Event, EventInfo, EventInfoSort, ManageEventBody, PublishedStatus, SectionUpdate, SortOrder, V2EventsResource } from '../../../@generated';
import { NavigationService } from '../../navigation/application/navigation.service';

@Injectable({
    providedIn: 'root',
})
export class EventService {
    private eventsResource = inject(V2EventsResource);
    private logger = inject(Logger);
    private navigationService = inject(NavigationService);

    public getEventInfos(
        page: number,
        pageSize: number,
        order: SortOrder | undefined,
        sort: EventInfoSort | undefined,
        year: number | undefined,
        query: string | undefined,
        status: PublishedStatus | undefined
    ): Observable<Page<EventInfo>> {
        return this.eventsResource.getEventInfos(page, pageSize, order, sort, year, query, status)
            .pipe(
                catchError(e => {
                    this.logger.apiError('Get event infos failed', e);
                    return of({ count: 0, results: [] });
                })
            );
    }

    public getLatestEvents(): Observable<EventInfo[]> {
        return this.eventsResource.getEventInfos(1, 4, SortOrder.Descending, EventInfoSort.Date).pipe(
            map(({ results }) => results),
            shareReplay(1),
            catchError(e => {
                this.logger.apiError('Get latest events failed', e);
                return of([]);
            })
        );
    }

    public getUpcomingEvents(): Observable<EventInfo[]> {
        return this.eventsResource.getEventInfos(1, 4, SortOrder.Ascending, EventInfoSort.Date, undefined, undefined, undefined, true).pipe(
            map(({ results }) => results),
            shareReplay(1),
            catchError(e => {
                this.logger.apiError('Get upcoming events failed', e);
                return of([]);
            })
        );
    }

    public getAllEventInfos(): Observable<EventInfo[]> {
        return this.eventsResource.getEventInfos(1, 999999, SortOrder.Ascending, EventInfoSort.Title).pipe(
            shareReplay(1),
            map(({ results }) => results),
            catchError(e => {
                this.logger.apiError('Get upcoming events failed', e);
                return of([]);
            })
        );
    }

    public getNonChildEventInfos(): Observable<EventInfo[]> {
        return this.eventsResource.getEventInfos(
            1, 999999, SortOrder.Ascending, EventInfoSort.Title, undefined, undefined, undefined, undefined, true
        ).pipe(
            map(({ results }) => results),
            catchError(e => {
                this.logger.apiError('Get non child event infos failed', e);
                return of([]);
            })
        );
    }

    public getPrivateEventInfos(): Observable<EventInfo[]> {
        return this.eventsResource.getEventInfos(
            1, 999999, SortOrder.Ascending, EventInfoSort.Title, undefined, undefined, PublishedStatus.Private
        ).pipe(
            map(({ results }) => results),
            catchError(e => {
                this.logger.apiError('Get private event infos failed', e);
                return of([]);
            })
        );
    }

    public getEventBySlug(slug: string): Observable<Event> {
        return this.eventsResource.getEventBySlug(slug).pipe(
            shareReplay(1),
            catchError(e => {
                if (e.status === 403) {
                    this.navigationService.navigateToUnauthorized();
                } else if(e.status === 404) {
                    this.navigationService.navigateToNotFound();
                } else {
                    this.logger.apiError('Get event with slug failed', e);
                }

                return EMPTY;
            })
        );
    }

    public getEventById(id: number): Observable<Event> {
        return this.eventsResource.getEventById(id).pipe(
            shareReplay(1),
            catchError(e => {
                if (e.status === 403) {
                    this.navigationService.navigateToUnauthorized();
                } else if(e.status === 404) {
                    this.navigationService.navigateToNotFound();
                } else {
                    this.logger.apiError('Get event by id failed', e);
                }

                throw e;
            }),
        );
    }

    public createEvent(body: ManageEventBody): Promise<Event> {
        return firstValueFrom(this.eventsResource.createEvent(body).pipe(
            catchError(e => {
                this.logger.apiError('Create event failed', e);
                return throwError(() => e);
            })
        ));
    }

    public updateEvent(id: number, body: ManageEventBody): Promise<Event> {
        return firstValueFrom(this.eventsResource.updateEvent(id, body).pipe(
            catchError(e => {
                this.logger.apiError('Update event failed', e);
                return throwError(() => e);
            })
        ));
    }

    public deleteEvent(id: number) {
        return firstValueFrom(this.eventsResource.deleteEvent(id).pipe(
            catchError(e => {
                this.logger.apiError('Delete event failed', e);
                return of({});
            })
        ));
    }

    public updateSections(eventId: number, sections: SectionUpdate[]) {
        return firstValueFrom(this.eventsResource.updateSections(eventId, { sections }).pipe(
            catchError(e => {
                this.logger.apiError('Sections update failed', e);
                return of({});
            })
        ))
    }
}
